import { createSlice } from '@reduxjs/toolkit'
import {
  appSettingsSliceConfig,
  appSettingsSliceExtraReducers,
} from 'apps/formsCreationKit/store/reducers/settings/appSettings'
import {
  addPdf,
  resetPdf,
  createElement,
  updateElement,
  deleteElement,
  selectElement,
  moveTable,
  createTable,
  deleteTable,
  addTableColumn,
} from 'apps/studio/store/actions/sharedActions'
import { studioSettingsActions } from 'apps/studio/store/reducers/settings/studioSettings'
const { setActiveMode } = studioSettingsActions
import {
  copyEntry,
  loadFormData,
} from 'apps/formsCreationKit/store/actions/sharedActions'
import { deterministicGuid } from 'apps/builder/lib/hash'

const fetchInitialConfigSetting = setting => {
  const { initialOnboardingState = {} } = window
  return initialOnboardingState[setting]
}

const updateActiveEntryAndSheet = ({
  state,
  activeEntry = null,
  activeSheet = null,
}) => {
  const update = {
    ...state,
    activeEntry,
    activeSheet,
  }
  if (!activeEntry && !activeSheet) {
    update.activeModal = ''
  }
  return update
}

const studioAppSettingsConfig = {
  ...appSettingsSliceConfig,
  initialState: {
    ...appSettingsSliceConfig.initialState,
    whiteLabeled: fetchInitialConfigSetting('white_labeled') ?? false,
  },
  reducers: {
    ...appSettingsSliceConfig.reducers,
    resetActiveEntryAndSheet: state => {
      return updateActiveEntryAndSheet({ state })
    },
    selectSheet: (state, action) => {
      const { sheet_id } = action.payload
      return updateActiveEntryAndSheet({ state, activeSheet: sheet_id })
    },
  },
  extraReducers: builder => {
    builder.addCase(createElement, (state, action) => {
      const { element = {}, entry = {} } = action.payload
      return updateActiveEntryAndSheet({
        state,
        activeEntry: element.entry_guid,
        activeSheet: entry.sheet_id,
      })
    })
    builder.addCase(updateElement, (state, action) => {
      const { entry = {} } = action.payload
      return updateActiveEntryAndSheet({
        state,
        activeEntry: entry.guid,
        activeSheet: entry.sheet_id,
      })
    })
    builder.addCase(selectElement, (state, action) => {
      const { entry_guid, sheet_id } = action.payload
      return updateActiveEntryAndSheet({
        state,
        activeEntry: entry_guid,
        activeSheet: sheet_id,
      })
    })
    builder.addCase(setActiveMode, (state, action) => {
      const { activeMode } = action.payload
      if (activeMode === 'Test') {
        return {
          ...state,
          activeEntry: null,
          activeSheet: null,
        }
      }
      return state
    })
    builder.addCase(createTable, (state, action) => {
      const { id: keyFieldSheetId } = action.payload
      return updateActiveEntryAndSheet({
        state,
        activeEntry: null,
        activeSheet: keyFieldSheetId,
      })
    })
    builder.addCase(addTableColumn, (state, action) => {
      const { newEntryGuid, tableSheetId } = action.payload
      // Bulk adds from the settings panel don't carry newEntryGuid — keep the
      // selection on the table level so the panel stays visible.
      if (!newEntryGuid || !tableSheetId) return state
      return updateActiveEntryAndSheet({
        state,
        activeEntry: newEntryGuid,
        activeSheet: tableSheetId,
      })
    })
    builder.addCase(deleteElement, (state, action) => {
      const { sheet = {} } = action.payload
      // If a sheet id is present, that means the deleted element was the last on the page and thus the sheet is being deleted
      return updateActiveEntryAndSheet({
        state,
        activeSheet: sheet.id ? null : state.activeSheet,
      })
    })
    builder.addCase(deleteTable, state => updateActiveEntryAndSheet({ state }))
    builder.addCase(addPdf, state => updateActiveEntryAndSheet({ state }))
    builder.addCase(resetPdf, state => updateActiveEntryAndSheet({ state }))
    builder.addCase(loadFormData, state => updateActiveEntryAndSheet({ state }))
    builder.addCase(moveTable, (state, action) => {
      const { sheet_id } = action.payload
      return updateActiveEntryAndSheet({
        state,
        activeSheet: sheet_id,
      })
    })
    builder.addCase(copyEntry, (state, action) => {
      const { guid, seed } = action.payload
      return updateActiveEntryAndSheet({
        state,
        activeEntry: deterministicGuid(guid, seed),
        activeSheet: state.activeSheet,
      })
    })

    return appSettingsSliceExtraReducers(builder)
  },
}

const appSettingsSlice = createSlice(studioAppSettingsConfig)
export const { actions: appSettingsActions, reducer: appSettingsReducer } =
  appSettingsSlice
